import * as React from "react";
import * as moment from "moment";
import { EmailTemplateApiModel } from "../apiModels/EmailTemplateApiModel";
import { MergeFieldApiModel } from "../apiModels/MergeFieldApiModel";
import * as Noty from "noty";
import { Editor } from "@tinymce/tinymce-react";
import { TestCampaignEmailModal } from "./TestCampaignEmailModal";
import { RouteComponentProps } from "react-router";
import { RouteHelper } from "../helpers/RouteHelper";
import { ErrorModal } from "./ErrorModal";
import { HttpErrorHelper } from "../helpers/HttpErrorHelper";
import { CampaignApiModel } from "../apiModels/CampaignApiModel";
import { RioInput, RioConfirmModal, RioValidator, RioResources, RioIconButton, RioApiPost, RioApiGet, RioIf, RioExpandingPanel, RioModal, RioDateHelpers} from "rio.react.all";


interface ICampaignEmailEditorModalProps extends Partial<RouteComponentProps>{
    id?: string;
    copyId?: string
    onClose: () => void;
}

interface ICampaignEmailEditorModalState {
    campaign: CampaignApiModel
    emailTemplateId?: number;
    emailContent: string;
    title: string;
    author: string;
    lastEditedOn: string;
    lastEditedBy: string;
    createdOn: string;
    mergeFields: Array<MergeFieldApiModel>
    closeClicked: boolean;
    testClicked: boolean;

    newCampaignSaved: boolean; //needed to add test button after save.

    changesMade: boolean;

    responseError: string;

    textFieldValue: string;
}

export class CampaignEmailEditorModal extends React.Component<ICampaignEmailEditorModalProps, ICampaignEmailEditorModalState> {
    constructor(props: Readonly<ICampaignEmailEditorModalProps>) {
        super(props);

        this.state = {
            campaign: new CampaignApiModel(),
            emailContent: "",
            title: "",
            author: "",
            lastEditedOn: "",
            lastEditedBy: "",
            emailTemplateId: null,
            createdOn: "",
            mergeFields: [],
            closeClicked: false,
            testClicked: false,
            newCampaignSaved: false,
            changesMade: false,
            responseError: null,
            textFieldValue: ""
        }
    }

    private campaignId: number;
    private copyId: number;
    private validator = new RioValidator();
    
    private editor: Editor;

    render() {
        return (
            <React.Fragment>
                <RioModal visible={true} onCancel={() => this.handleCloseClicked()} className="large" backgroundClickDisabled={true}>

                    <RioExpandingPanel resource={RioResources.find("ComposeEmail")} expandedDefault={true} disableToggle={true}>
                        <div className="no-padding">
                            <div className="form-row emailTitleBox">
                                <label className="emailTitleLabel">{RioResources.find("Title").text}<span className="required">*</span></label>
                                <RioInput name="Title" type="text" className="emailTitleInput" value={this.state.title} placeholder={RioResources.find("EmailTitlePlaceholder").text} onChange={(e) => this.handleTitleChanged(e.target.value)} validator={this.validator} validations={{ required: true, maxLength: 255 }} />
                            </div>
                        </div>

                        <Editor
							ref={(editor) => this.editor = editor}
							tinymceScriptSrc="../lib/tinymce/tinymce.min.js"
							//tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/5.10.5/tinymce.min.js"
                            initialValue={this.state.emailContent}
                            onChange={(e) => this.handleEditorChanged(e)}
                            init={{
                                plugins: 'link image code table mentions lists',
                                toolbar: 'mergefields | undo redo | bold italic underline strikethrough | fontsizeselect | forecolor forecolorpicker backcolor backcolorpicker | alignleft aligncenter alignright justify | numlist bullist | sub sup | link unlink image | code',
                                fontsize_formats: '8px 10px 12px 14px 16px 18px 20px 22px 24px 26px 28px 30px 32px 34px 36px',
                                font_formats: 'Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Segoe UI=Segoe UI, Lucida Grande, Tahoma, sans-serif; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats',
                                height: 600,
                                convert_urls: false,
                                setup: (editor: any) => {
                                    editor.ui.registry.addMenuButton('mergefields', {
                                        text: 'Merge Fields',
                                        fetch: (callback: any) => {
                                            var items = []
                                            for (var i = 0; i < this.state.mergeFields.length; i++) {
                                                var item = {
                                                    type: 'menuitem',
                                                    text: this.state.mergeFields[i].mergeFieldName,
                                                    value: this.state.mergeFields[i].mergeFieldSymbol,
                                                    onSetup: function () {
                                                        var $this = this;
                                                        this.onAction = function () {
                                                            editor.insertContent($this.data.value);
                                                        };
                                                    },
                                                }
                                                items.push(item)
                                            }
                                            callback(items);
                                        }
                                    });
                                },
                                
                                images_upload_handler: (blobInfo: any, success: any, failure: any) => {
                                    let formData: FormData = new FormData();

                                    var xhr = new XMLHttpRequest();
                                    xhr.withCredentials = false;
                                    xhr.open('POST', '/api/Image/UploadImage');
                                    xhr.onload = function () {
                                        var json;

                                        if (xhr.status != 200) {
                                            failure('HTTP Error: ' + xhr.status);
                                            return;
                                        }
                                        json = JSON.parse(xhr.responseText);

                                        if (!json || typeof json.location != 'string') {
                                            failure('Invalid JSON: ' + xhr.responseText);
                                            return;
                                        }
                                        success(json.location);
                                    };
                                    formData.append('file', blobInfo.blob(), blobInfo.filename());
                                    xhr.send(formData);
                                }
                            }}

                        />
                        
                    </RioExpandingPanel>

                    <div className="buttons emailComposerButtons">
                        <RioIf condition={!!this.campaignId || !!this.state.newCampaignSaved} then={
                            <RioIconButton className="emailTestButton" resource={RioResources.find("Test")} onClick={() => this.handleTestClicked()} disabled={this.state.changesMade} hasPermission={true} />
                        } />
                        <RioIconButton resource={RioResources.find("Save")} onClick={() => this.handleSaveClicked()} hasPermission={true} />
                        <RioIconButton resource={RioResources.find("Close")} onClick={() => this.handleCloseClicked()} hasPermission={true} />
                    </div>

                    <RioIf condition={!!this.state.closeClicked} then={
                        <RioConfirmModal text={RioResources.find("DiscardUnsavedChanges")} onConfirmModalClosed={(result) => this.handleConfirmClose(result)} className="confirmModal" />
                    } />

                    <RioIf condition={!!this.state.testClicked} then={
                        <TestCampaignEmailModal emailSubject={this.state.title} emailContent={this.state.emailContent} campaignID={this.campaignId} onClose={() => this.setState({testClicked: false})} />
                    } />

                    <RioIf condition={!!this.state.responseError} then={
                        <ErrorModal text={this.state.responseError} onErrorModalClosed={() => this.setState({ responseError: null })} />
                    } />

                </RioModal>

            </React.Fragment>
        );
    }

    getSymbol() {
        return this.state.mergeFields[0].mergeFieldSymbol;
    }

    componentDidMount(): void {
        this.campaignId = parseInt(this.props.id);
        this.copyId = parseInt(this.props.copyId);
        this.getCampaign();
        this.getEmailDetails();
        this.getMergeFields();
    }

    getCampaign() {
        if (isNaN(this.campaignId)) {
            this.setState({
                campaign: new CampaignApiModel()
            });
        } else {
            RioApiGet.asPromise<CampaignApiModel>("/api/Campaign/" + this.campaignId.toString())
                .then(response => {
                    if (!!response) {
                        this.setState({
                            campaign: response,
                        });
                    }
                })
                .catch((response) => {
                    var error = HttpErrorHelper.handleHttpError(response, this, this.validator); //handles validator errors if validator passed
                    this.setState({ responseError: error });
                });
        }
    }

    getEmailDetails() {
        if (isNaN(this.campaignId)) {
            if (isNaN(this.copyId)) {
                RioApiGet.asPromise<EmailTemplateApiModel>("/api/CampaignEmail/GetDefaultTemplate")
                    .then(response => {
                        if (!!response) {
                            this.setState({
                                emailContent: response.emailContent,
                                textFieldValue: response.emailContent
                            });
                        }
                    })
                    .catch((response) => {
                        var error = HttpErrorHelper.handleHttpError(response, this, this.validator); //handles validator errors if validator passed
                        this.setState({ responseError: error });
                    });
            } else {
                RioApiGet.asPromise<EmailTemplateApiModel>(`/api/CampaignEmail/GetTemplate/${this.copyId}`)
                    .then(response => {
                        if (!!response) {
                            this.setState({
                                emailContent: response.emailContent,
                                textFieldValue: response.emailContent
                            });
                        }
                    })
                    .catch((response) => {
                        var error = HttpErrorHelper.handleHttpError(response, this, this.validator); //handles validator errors if validator passed
                        this.setState({ responseError: error });
                    });
            }   
        } else {
            RioApiGet.asPromise<EmailTemplateApiModel>(`/api/CampaignEmail/GetTemplate/${this.campaignId}`)
                .then(response => {
                    if (!!response) {
                        this.setState({

                            emailTemplateId: response.emailTemplateID,
                            emailContent: response.emailContent,
                            textFieldValue: response.emailContent,
                            title: response.title,
                            author: response.author,
                            lastEditedBy: response.lastEditedBy,
                            lastEditedOn: response.lastEditedOn,
                            createdOn: response.createdOn
                        });

                        
                    }
                })
                .catch((response) => {
                    var error = HttpErrorHelper.handleHttpError(response, this, this.validator); //handles validator errors if validator passed
                    this.setState({ responseError: error });
                });
        }
        
    }

    getMergeFields() {
        RioApiGet.asPromise<Array<MergeFieldApiModel>>("/api/MergeField/Get")
            .then(response => {
                if (!!response) {
                    this.setState({
                        mergeFields: response
                    });
                }
            })
            .catch((response) => {
                var error = HttpErrorHelper.handleHttpError(response, this, this.validator); //handles validator errors if validator passed
                this.setState({ responseError: error });
            });
    }

    handleCloseClicked() {
        if (this.state.campaign.startDate >= moment().toISOString() && !!this.state.changesMade) {
            this.setState({
                closeClicked: true
            });
        } else {
            this.handleConfirmClose(true);
        }
        
    }

    handleConfirmClose(result: boolean) {
        if (result) {
            this.props.onClose();
            if (isNaN(this.copyId)) {
                RouteHelper.up(this as any);
            } else {
                //Can't use RouteHelper.up because copy takes us down 2 levels. Perhaps extend RouteHelper later.
                this.props.history.push("/campaigns");
            }
            
        } else {
            this.setState({
                closeClicked: false
            });
        }
    }

    handleTestConfirmed(result: boolean) {
        if (result) {
            this.props.onClose();
        } else {
            this.setState({
                testClicked: false
            });
        }
    }

    handleTestClicked() {
        if (this.validator.isValid(this)) {
            this.setState({
                testClicked: true
            });
        }
    }

    handleTitleChanged(value: string) {
        this.setState({
            title: value,
            changesMade: true
        })
    }

    handleSaveClicked() {
        var editorContent = this.state.textFieldValue;
        //strip out all html, and trim it to see if any characters have been entered.
        const strippedEmail = editorContent.replace(/(<([^>]+)>)/ig, "").trim();

        if (strippedEmail.length === 0) {
            return;
        }

        const email = new EmailTemplateApiModel();
        email.emailTemplateID = !!this.state.emailTemplateId ? this.state.emailTemplateId : 0;
        email.emailContent = editorContent;
        email.campaignID = isNaN(this.campaignId) ? 0 : this.campaignId;
        email.title = this.state.title;
        email.createdOn = !!this.state.createdOn ? this.state.createdOn : RioDateHelpers.toISODateTime(moment().toISOString());

        RioApiPost.asPromise<EmailTemplateApiModel>("/api/CampaignEmail/TemplateAddUpdate", email)
            .then((response) => {
                if (isNaN(this.campaignId) || this.campaignId == undefined) {
                    this.campaignId = response.campaignID;
                    this.setState({
                        emailTemplateId: response.emailTemplateID,
                        newCampaignSaved: true
                    });
                }
                new Noty({
                    type: "success",
                    layout: "topCenter",
                    theme: "relax",
                    text: "<span class='notyIcon'>" + RioResources.find("Saved").image + "</span> " + RioResources.find("Saved").text,
                    timeout: 3000,
                    closeWith: ['click']
                }).show();
                this.setState({
                    changesMade: false
                });
            })
            .catch((response) => {
                this.validator.addModelErrors(this, response.errors);
                //var error = HttpErrorHelper.handleHttpError(response, this, this.validator); //handles validator errors if validator passed
                //this.setState({ responseError: error });
            });
        this.setState({
            emailContent: editorContent
        });
    }

    handleEditorChanged(e: any) {
        let editorContent = e.target.getContent();
        if (editorContent != this.state.textFieldValue) {
            this.setState({
                textFieldValue: editorContent,
                changesMade: true
            });
        } 
    }
}
